/**
 * Start from one of your own shells instead of a blank page.
 *
 * THE GALLERY IS YOUR OWN DOCUMENTS, not a catalogue somebody shipped. A template in a quality
 * system is the approved SOP skeleton -- the heading structure, the signature block, the
 * confidentiality wording your auditor expects -- and nobody outside the organisation can write
 * that. So this lists documents somebody here ticked a box on, each one still a real controlled
 * record with an owner and a revision.
 *
 * IT SHOWS WHICH RECORD EACH ONE IS, because "SOP Template" twice is the normal state of a quality
 * system and the document number is the only thing that tells them apart.
 *
 * AND IT SAYS WHAT STARTING FROM ONE DOES. A copy is taken now; editing the template afterwards
 * changes nothing that was started from it. That is the only behaviour a controlled document can
 * have, and it is the opposite of what "template" means in most software, so it is said rather
 * than assumed.
 *
 * IT IS ALSO THE PICKER FOR "CREATE FROM EXISTING DOCUMENT", which is the same screen asking a
 * wider question: any document the reader can see rather than only the shells somebody published.
 * One dialog with different words, not two, because the part that would rot in a copy is the
 * explanation of what copying does -- and that is the part both need to get right.
 */
import React from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from "@mui/material";

import { CHROME_FONT } from "./EditorToolbar";
import { Grey3, Grey5, Grey17, Blue3 } from "../../theme/styles";

export type Template = {
    USID: string;
    Name: string;
    ObjectID?: string | null;
    RecordName?: string | null;
    DisplayRevision?: string | null;
    Status?: string | null;
    Preview?: string | null;
    Characters?: number | null;
};

export default function TemplateDialog({ open, templates, loading, onStart, onClose,
                                        title, note, empty, search, onSearch }: {
    open: boolean;
    templates: Template[];
    loading: boolean;
    onStart: (template: Template) => void;
    onClose: () => void;
    /** Overridden by "Create from existing document"; defaults are the template gallery's. */
    title?: string;
    /** What choosing one does. Said rather than assumed -- see the note at the top. */
    note?: React.ReactNode;
    empty?: React.ReactNode;
    /** Present only when the list is searchable, which the gallery is not: a handful of shells
     *  does not need a search box, and every document in the tenant does. */
    search?: string;
    onSearch?: (value: string) => void;
}) {
    const [chosen, setChosen] = React.useState<string | null>(null);
    React.useEffect(() => { if (open) setChosen(templates[0]?.USID ?? null); }, [open, templates]);

    const picked = templates.find(one => one.USID === chosen) || null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontFamily: CHROME_FONT, fontSize: 18, fontWeight: 500,
                               color: Grey17 }}>
                {title ?? "Start from a template"}
            </DialogTitle>
            <DialogContent>
                {onSearch ? (
                    <TextField size="small" fullWidth autoFocus value={search ?? ""}
                               onChange={event => onSearch(event.target.value)}
                               placeholder="Search by document name, record number or record name"
                               sx={{ mb: 1, mt: 0.5,
                                     "& .MuiInputBase-input": { fontFamily: CHROME_FONT, fontSize: 14 } }}/>
                ) : null}
                {loading ? (
                    <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 14, color: Grey5, py: 3 }}>
                        {onSearch ? "Looking…" : "Looking for templates…"}
                    </Typography>
                ) : templates.length === 0 ? (
                    // AN EMPTY GALLERY TELLS YOU HOW TO FILL IT. "No templates" on its own is a
                    // dead end for a feature whose whole point is that the templates are yours.
                    <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 14, color: Grey5, py: 3,
                                      lineHeight: 1.7 }}>
                        {empty ?? (<>
                            Nothing in this account has been marked as a template yet. Open a
                            document you want others to start from and choose
                            {" "}<b>File ▸ Save as template</b> — it stays an ordinary controlled
                            document, and appears here as well.
                        </>)}
                    </Typography>
                ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, py: 1 }}>
                        {templates.map(template => (
                            <Box key={template.USID}
                                 onClick={() => setChosen(template.USID)}
                                 onDoubleClick={() => onStart(template)}
                                 sx={{
                                     width: 184, cursor: "pointer", borderRadius: 1,
                                     border: "1px solid",
                                     borderColor: chosen === template.USID ? Blue3 : Grey3,
                                     boxShadow: chosen === template.USID
                                         ? `0 0 0 1px ${Blue3}` : "none",
                                     overflow: "hidden", boxSizing: "border-box",
                                 }}>
                                {/* THE OPENING WORDS, at the size the cards on the landing page
                                    use: what a template looks like is its shape, and its shape is
                                    its headings. */}
                                <Box sx={{ height: 128, p: 1, overflow: "hidden",
                                           fontSize: "5.5px", lineHeight: 1.45, color: Grey17,
                                           whiteSpace: "pre-wrap", backgroundColor: "#FFFFFF" }}>
                                    {template.Preview || ""}
                                </Box>
                                <Box sx={{ px: 1, py: 0.75, borderTop: "1px solid",
                                           borderColor: Grey3 }}>
                                    <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 13,
                                                      color: Grey17, fontWeight: 500,
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap" }}>
                                        {template.Name}
                                    </Typography>
                                    <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 11.5,
                                                      color: Grey5, overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap" }}>
                                        {[template.ObjectID,
                                          template.DisplayRevision ? `Rev ${template.DisplayRevision}` : null]
                                            .filter(Boolean).join(" · ")}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
                {templates.length > 0 && (
                    <Typography sx={{ fontFamily: CHROME_FONT, fontSize: 12, color: Grey5, mt: 2,
                                      lineHeight: 1.6 }}>
                        {note ?? (<>
                            A copy is taken now. Changing the template afterwards does not change
                            anything already started from it.
                        </>)}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ fontFamily: CHROME_FONT }}>Cancel</Button>
                <Button variant="contained" disableElevation disabled={!picked}
                        onClick={() => picked && onStart(picked)}
                        sx={{ fontFamily: CHROME_FONT, textTransform: "none" }}>
                    Start
                </Button>
            </DialogActions>
        </Dialog>
    );
}
